import "./App.css";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import HXCancellationRule from "./Pages/HXCancellationRule";
import Login from "./Pages/Login";
import AdminHeader from "./Pages/global/AdminHeader";
import Home from "./Pages/Home";
import HXKiller from "./Pages/HXKiller";
import AgencyBookingReport from "./Pages/AgencyBookingReport";
import ScheduleChange from "./Pages/ScheduleChange";
import ScheduleChangeRule from "./Pages/ScheduleChangeRule";
import AgencyBookingReportRule from "./Pages/AgencyBookingReportRule";
import DisplayLogs from "./Pages/DisplayLogs";
import { useEffect, useState } from "react";
import ManageCustomer from "./Pages/ManageCustomers";
import Customer from "./Pages/Customer";
import AdminFooter from "./Pages/global/AdminFooter";
import AdminProfile from "./Pages/AdminProfile";
import ForgotPassword from "./Pages/ForgotPassword";
import ChangePassword from "./Pages/ChangePassword";
import ManageItinerary from "./Pages/ManageItinerary";
import DisplayItinerary from "./Pages/DisplayItinerary";
import { jwtDecode } from "jwt-decode";
import ManageUsers from "./Pages/ManageUsers";
import User from "./Pages/User";
import { useDispatch, useSelector } from "react-redux";
import { getUserPermission } from "./redux/actions/AdminAction";
import TicketReport from "./Pages/TicketReport";
import NewTicketReport from "./Pages/NewTicketReport";
import MultiPccTicketReport from "./Pages/MultiPccTicketReport";
import PrivilegedNewTicketReport from "./Pages/PrivilegedNewTicketReport";
import CustomerLogins from "./Pages/CustomerLogins";
import ManageCustomerLogins from "./Pages/ManageCustomerLogins";

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("userInfo"));
  const user_id = JSON.parse(localStorage.getItem("user_id"));
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [decodedToken, setDecodedToken] = useState(null);
  const [checkUserPermission, setCheckUserPermission] = useState(
    () => () => false
  ); // Default to a no-op function

  const GetUserPermissionData = useSelector(
    (state) => state.GetUserPermissionData
  );
  const { userPermissionInfo } = GetUserPermissionData;

  const hasAccess = (allowedRoles) => allowedRoles.includes(userRole);

  // Get user permission
  useEffect(() => {
    if ((userRole === "user" || userRole === "admin") && user_id) {
      dispatch(getUserPermission(user_id));
    }
  }, [userRole, user_id]);

  useEffect(() => {
    setCheckUserPermission(() => (productId, permission) => {
      if (userRole === "superuser") {
        return true;
      }
      if (userRole === "admin") {
        // For admin users, only check permissions for product 105 which is Customer Logins
        if (productId === 105) {
          const productPermissions = userPermissionInfo?.find(
            (p) => p.product_id === productId
          );
          return productPermissions ? productPermissions[permission] : false;
        }
        // For all other products, admin has full access
        return true;
      }
      if (userRole === "user") {
        const productPermissions = userPermissionInfo?.find(
          (p) => p.product_id === productId
        );
        return productPermissions ? productPermissions[permission] : false;
      }
    });
  }, [userRole, userPermissionInfo]);

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const locator = searchParams.get("locator");
      const pcc = searchParams.get("pcc");

      if (locator) sessionStorage.setItem("pnr", locator);
      if (pcc) sessionStorage.setItem("pcc", pcc);

      if (location.pathname === "/managemyitineraryresult") {
        setCheckUserPermission(() => () => true);
        navigate("/managemyitineraryresult", { replace: true });
      }
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const role = decodedToken.role;
        setUserRole(role);
        setDecodedToken(decodedToken);
      } catch (error) {
        console.error("Invalid token or error decoding:", error);
        navigate("/");
      }
    }
    const checkTokenExpiration = () => {
      const tokenExpiration = localStorage.getItem("tokenExpiration");
      if (tokenExpiration && new Date(tokenExpiration) < new Date()) {
        // Token has expired
        localStorage.removeItem("userInfo");
        localStorage.removeItem("tokenExpiration");
        localStorage.removeItem("user_id");
        navigate("/", { replace: true });
      }
    };

    checkTokenExpiration();

    const interval = setInterval(checkTokenExpiration, 60000); // Check every minute

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [token, navigate]);

  return (
    <div>
      <Routes>
        <Route
          path="/"
          element={
            !token ? (
              <>
                <AdminHeader />
                <Login page="login" />
                <AdminFooter />
              </>
            ) : (
              <>
                <AdminHeader />
                <Navigate to="/home" replace />
                <AdminFooter />
              </>
            )
          }
        />
        <Route
          path="/forgotpassword"
          element={
            !token ? (
              <>
                <AdminHeader />
                <ForgotPassword />
                <AdminFooter />
              </>
            ) : (
              <>
                <AdminHeader />
                <Navigate to="/" />
                <AdminFooter />
              </>
            )
          }
        />
        <Route
          path="/changepassword"
          element={
            !token ? (
              <>
                <AdminHeader />
                <ChangePassword />
                <AdminFooter />
              </>
            ) : (
              <>
                <AdminHeader />
                <Navigate to="/" />
                <AdminFooter />
              </>
            )
          }
        />
        <Route
          path="/home"
          element={
            token ? (
              <>
                <AdminHeader />
                <Home
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/profile"
          element={
            token && hasAccess(["superuser", "admin", "user"]) ? (
              <>
                <AdminHeader />
                <AdminProfile />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/hxCancellationRules"
          element={
            token && hasAccess(["superuser", "admin", "user"]) ? (
              <>
                <AdminHeader />
                <HXKiller
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/hxCancellationRules/:id"
          element={
            token && hasAccess(["superuser", "admin", "user"]) ? (
              <>
                <AdminHeader />
                <HXCancellationRule decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/agencyBookingReportRules"
          element={
            token &&
            hasAccess(["superuser", "admin", "user"]) &&
            checkUserPermission(106, "can_view") ? (
              <>
                <AdminHeader />
                <AgencyBookingReport
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/agencyBookingReportRules/:id"
          element={
            token &&
            hasAccess(["superuser", "admin", "user"]) &&
            (checkUserPermission(106, "can_add") ||
              checkUserPermission(106, "can_edit")) ? (
              <>
                <AdminHeader />
                <AgencyBookingReportRule decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/scheduleChangeRules"
          element={
            token &&
            hasAccess(["superuser", "admin", "user"]) &&
            checkUserPermission(107, "can_view") ? (
              <>
                <AdminHeader />
                <ScheduleChange
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
            
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
         <Route
            path="/scheduleChangeRules/:id"
          element={
            token &&
            hasAccess(["superuser", "admin", "user"]) &&
            (checkUserPermission(107, "can_add") ||
              checkUserPermission(107, "can_edit")) ? (
              <>
                <AdminHeader />
                 <ScheduleChangeRule decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
         <Route
          path="/viewlogs"
          element={
            token && hasAccess(["superuser", "admin", "user"]) ? (
              <>
                <AdminHeader />
                <DisplayLogs
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
            
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/managecustomer"
          element={
            token && hasAccess(["superuser", "admin", "user"]) ? (
              <>
                <AdminHeader />
                <ManageCustomer
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/managecustomer/:id"
          element={
            token &&
            hasAccess(["superuser", "admin", "user"]) &&
            (checkUserPermission(104, "can_add") ||
              checkUserPermission(104, "can_edit")) ? (
              <>
                <AdminHeader />
                <Customer decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/managemyitinerary"
          element={
            <>
              <AdminHeader />
              <ManageItinerary decodedToken={decodedToken} />
              <AdminFooter />
            </>
          }
        />
        <Route
          path="/managemyitineraryresult"
          element={
            <>
              <AdminHeader />
              <DisplayItinerary
                decodedToken={decodedToken}
                checkUserPermission={checkUserPermission}
              />
              <AdminFooter />
            </>
          }
        />
        <Route
          path="/manageusers"
          element={
            token && hasAccess(["superuser"]) ? (
              <>
                <AdminHeader />
                <ManageUsers decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/manageusers/:id"
          element={
            token && hasAccess(["superuser"]) ? (
              <>
                <AdminHeader />
                <User decodedToken={decodedToken} />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ticketreport"
          element={
            token && checkUserPermission(103, "can_view") ? (
              <>
                <AdminHeader />
                <TicketReport
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="/newticketreport"
          element={
            token && checkUserPermission(103, "can_view") ? (
              <>
                <AdminHeader />
                <NewTicketReport
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="/multipccticketreport"
          element={
            token && checkUserPermission(103, "can_view") ? (
              <>
                <AdminHeader />
                <MultiPccTicketReport
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="/privilegednewticketreport"
          element={
            token ? (
              <>
                <AdminHeader />
                <PrivilegedNewTicketReport
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="/managecustomerlogins"
          element={
            !token ? (
              <Navigate to="/" replace />
            ) : hasAccess(["superuser", "admin"]) &&
              checkUserPermission(105, "can_view") ? (
              <>
                <AdminHeader />
                <ManageCustomerLogins
                  decodedToken={decodedToken}
                  checkUserPermission={checkUserPermission}
                />
                <AdminFooter />
              </>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="/managecustomerlogins/:id"
          element={
            token &&
            hasAccess(["superuser", "admin"]) &&
            (checkUserPermission(105, "can_add") ||
              checkUserPermission(105, "can_edit")) ? (
              <>
                <AdminHeader />
                <CustomerLogins />
                <AdminFooter />
              </>
            ) : (
              <>
                <Navigate to="/home" replace />
              </>
            )
          }
        />
      </Routes>
    </div>
  );
}
